import type { Shop, ShopProduct } from "@mall-mart/shared";

function baseUrl(serverWsUrl: string): string {
  return serverWsUrl.replace(/^ws(s)?:/, "http$1:");
}

export async function fetchShops(serverWsUrl: string): Promise<Shop[]> {
  const res: Response = await fetch(`${baseUrl(serverWsUrl)}/api/shops`);
  if (!res.ok) {
    throw new Error(`GET /api/shops failed: ${res.status}`);
  }
  const data: { shops: Shop[] } = await res.json();
  return data.shops;
}

// Session-side product cache: walking away from a store and back must not
// cost another round trip. Entries live for CACHE_TTL_MS; concurrent
// requests for the same store share one inflight fetch.
const CACHE_TTL_MS: number = 10 * 60 * 1000;
const productCache: Map<string, { products: ShopProduct[]; at: number }> = new Map();
const inflight: Map<string, Promise<ShopProduct[]>> = new Map();

export async function fetchProducts(serverWsUrl: string, shopId: string): Promise<ShopProduct[]> {
  const cached = productCache.get(shopId);
  if (cached !== undefined && Date.now() - cached.at < CACHE_TTL_MS) {
    return cached.products;
  }
  const pending: Promise<ShopProduct[]> | undefined = inflight.get(shopId);
  if (pending !== undefined) {
    return pending;
  }
  const request: Promise<ShopProduct[]> = (async () => {
    const res: Response = await fetch(`${baseUrl(serverWsUrl)}/api/shops/${shopId}/products`);
    if (!res.ok) {
      throw new Error(`GET /api/shops/${shopId}/products failed: ${res.status}`);
    }
    const data: { shopId: string; products: ShopProduct[] } = await res.json();
    productCache.set(shopId, { products: data.products, at: Date.now() });
    return data.products;
  })();
  inflight.set(shopId, request);
  try {
    return await request;
  } finally {
    inflight.delete(shopId);
  }
}
